import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "@tanstack/react-router";
import { deletionReferences } from "@wedding/api-client";
import type { EntityRecord, EntityType } from "@wedding/schemas";
import { Button, Skeleton } from "@wedding/ui";
import {
  CheckCircle2,
  ClipboardPenLine,
  Download,
  Filter,
  Plus,
  QrCode,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { api, apiFetch } from "../api";
import { moduleByPath, type TaskModuleType } from "../config";
import { EntityEditor } from "../components/EntityEditor";
import {
  EntityDetail,
  type ProjectMemberIdentity,
} from "../components/EntityDetail";
import { EntityTable } from "../components/EntityTable";
import { ModuleTodoSection } from "../components/ModuleTodoSection";
import { QrCodeDialog, type QrCodeTarget } from "../components/QrCodeDialog";
import { TaskBulkEditor } from "../components/TaskBulkEditor";
import { sortTasksByStatus, taskOwners } from "../task-list";

type ImportPreview = {
  fileName: string;
  headers: string[];
  sourceRows: Record<string, string>[];
  mapping: Record<string, string>;
  rows: Record<string, unknown>[];
  sourceCount: number;
  errors: string[];
  warnings: string[];
};

type ProjectMembers = { members: ProjectMemberIdentity[] };

function csvEscape(value: unknown) {
  const raw = value == null ? "" : String(value);
  const safe = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return /[",\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    maximumFractionDigits: 0,
  }).format(value / 100);

function parseCsv(text: string) {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter(Boolean);
  if (!lines.length)
    return { headers: [] as string[], rows: [] as Record<string, string>[] };
  const split = (line: string) => {
    const out: string[] = [];
    let value = "";
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];
      if (character === '"' && quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else if (character === '"') quoted = !quoted;
      else if (character === "," && !quoted) {
        out.push(value);
        value = "";
      } else value += character;
    }
    out.push(value);
    return out;
  };
  const headers = split(lines[0]!);
  const rows = lines
    .slice(1)
    .map((line) =>
      Object.fromEntries(
        split(line).map((value, index) => [
          headers[index] ?? `column${index}`,
          value,
        ]),
      ),
    ) as Record<string, string>[];
  return { headers, rows };
}

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
}

export function EntityPage() {
  const params = useParams({ strict: false }) as {
    workspaceId: string;
    section: string;
  };
  const config = moduleByPath[params.section];
  const location = useLocation();
  const routeSearch = location.search as Record<string, unknown>;
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState(new Set<string>());
  const [editing, setEditing] = useState<EntityRecord>();
  const [viewing, setViewing] = useState<EntityRecord>();
  const [open, setOpen] = useState(false);
  const [specialView, setSpecialView] = useState("");
  const [view, setView] = useState<"records" | "tasks" | "library">("records");
  const [feedback, setFeedback] = useState<{
    tone: "success" | "error";
    message: string;
  }>();
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreview>();
  const [deleteTarget, setDeleteTarget] = useState<EntityRecord>();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [advancedFilterState, setAdvancedFilterState] = useState({
    section: params.section,
    open: false,
  });
  const advancedFiltersOpen =
    advancedFilterState.section === params.section && advancedFilterState.open;
  const setAdvancedFiltersOpen = (open: boolean) =>
    setAdvancedFilterState({ section: params.section, open });
  const [qrTarget, setQrTarget] = useState<QrCodeTarget>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearch("");
    setFilters({});
    setSelected(new Set());
    setEditing(undefined);
    setViewing(undefined);
    setOpen(false);
    setAdvancedFiltersOpen(false);
    setSpecialView("");
    setView("records");
  }, [params.section]);

  useEffect(() => {
    if (
      !advancedFiltersOpen ||
      !window.matchMedia("(max-width: 900px)").matches
    )
      return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAdvancedFiltersOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [advancedFiltersOpen]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(undefined), 3500);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const query = useQuery({
    queryKey: [params.workspaceId, config?.type],
    queryFn: () =>
      api.list(params.workspaceId, config!.type).then((result) => result.data),
    enabled: Boolean(config),
  });
  const members = useQuery({
    queryKey: [params.workspaceId, "members"],
    queryFn: () =>
      apiFetch<ProjectMembers>(`/workspaces/${params.workspaceId}/members`),
  });

  useEffect(() => {
    if (!config) return;
    const nextFilters = Object.fromEntries(
      config.fields
        .filter(
          (field) =>
            field.type === "select" &&
            typeof routeSearch[field.key] === "string",
        )
        .map((field) => [field.key, String(routeSearch[field.key])]),
    );
    setFilters(nextFilters);
    setSpecialView(
      typeof routeSearch.view === "string" ? routeSearch.view : "",
    );
  }, [config, location.href]);

  useEffect(() => {
    const recordId =
      typeof routeSearch.record === "string" ? routeSearch.record : "";
    if (!recordId || !query.data) return;
    setViewing(query.data.find((item) => item.id === recordId));
  }, [query.data, routeSearch.record]);
  const relationTypes = useMemo(
    () => [
      ...new Set(
        (config?.fields ?? [])
          .map((field) => field.relationType)
          .filter((type): type is EntityType => Boolean(type)),
      ),
    ],
    [config],
  );
  const relationQueryKey = [
    params.workspaceId,
    "relations",
    relationTypes.join(","),
  ] as const;
  const related = useQuery({
    queryKey: relationQueryKey,
    queryFn: async () =>
      Object.fromEntries(
        await Promise.all(
          relationTypes.map(async (type) => [
            type,
            (await api.list(params.workspaceId, type)).data,
          ]),
        ),
      ) as Partial<Record<EntityType, EntityRecord[]>>,
    enabled: relationTypes.length > 0,
  });

  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      editing
        ? api.update(
            params.workspaceId,
            config!.type,
            editing.id,
            data,
            editing.version,
          )
        : api.create(params.workspaceId, config!.type, data),
    onSuccess: async (result) => {
      client.setQueryData<EntityRecord[]>(
        [params.workspaceId, config!.type],
        (old) =>
          editing
            ? old?.map((item) => (item.id === editing.id ? result.data : item))
            : [result.data, ...(old ?? [])],
      );
      const rsvpToken = (result.data as EntityRecord & { rsvpToken?: string })
        .rsvpToken;
      if (config!.type === "guests" && rsvpToken) {
        await copyText(`${window.location.origin}/rsvp/${rsvpToken}`);
        setFeedback({
          tone: "success",
          message: "宾客已保存，专属邀请链接已复制",
        });
      } else {
        setFeedback({ tone: "success", message: `${config!.singular}已保存` });
      }
      if (
        [
          "budgets",
          "vendors",
          "core-vendors",
          "decorations",
          "stationery-gifts",
        ].includes(config!.type)
      )
        await Promise.all(
          [
            "budgets",
            "vendors",
            "core-vendors",
            "decorations",
            "stationery-gifts",
            "dashboard",
          ].map((type) =>
            client.invalidateQueries({
              queryKey: [params.workspaceId, type],
            }),
          ),
        );
      setOpen(false);
      setEditing(undefined);
    },
  });
  const invite = useMutation({
    mutationFn: ({ guest }: { guest: EntityRecord; action: "copy" | "qr" }) =>
      apiFetch<{ token: string; permanent: boolean }>(
        `/workspaces/${params.workspaceId}/guests/${guest.id}/rsvp-link`,
        { method: "POST", body: "{}" },
      ),
    onSuccess: async (result, { guest, action }) => {
      const url = `${window.location.origin}/rsvp/${result.token}`;
      if (action === "qr")
        setQrTarget({
          title: `${String(guest.data.name ?? "宾客")}的专属邀请`,
          url,
        });
      else {
        await copyText(url);
        setFeedback({
          tone: "success",
          message: "专属邀请链接已复制；以后再次复制仍是同一链接",
        });
      }
      await client.invalidateQueries({
        queryKey: [params.workspaceId, "guests"],
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "邀请链接生成失败",
      }),
  });
  const guestRegistration = useMutation({
    mutationFn: (action: "copy" | "qr") =>
      apiFetch<{ token: string; permanent: boolean }>(
        `/workspaces/${params.workspaceId}/guest-registration-link`,
        { method: "POST", body: "{}" },
      ),
    onSuccess: async (result, action) => {
      const url = `${window.location.origin}/rsvp/${result.token}`;
      if (action === "qr") setQrTarget({ title: "宾客自助登记", url });
      else {
        await copyText(url);
        setFeedback({
          tone: "success",
          message: "固定自助登记链接已复制，可长期重复使用",
        });
      }
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message:
          error instanceof Error ? error.message : "自助登记链接生成失败",
      }),
  });
  const remove = useMutation({
    mutationFn: ({ item, cascade }: { item: EntityRecord; cascade: boolean }) =>
      api.remove(
        params.workspaceId,
        config!.type,
        item.id,
        item.version,
        cascade,
      ),
    onSuccess: (_, { item, cascade }) => {
      client.setQueryData<EntityRecord[]>(
        [params.workspaceId, config!.type],
        (old) => old?.filter((record) => record.id !== item.id),
      );
      if (cascade)
        void client.invalidateQueries({ queryKey: [params.workspaceId] });
      setDeleteTarget(undefined);
    },
  });
  const deleteReferences = deletionReferences(remove.error);

  const relationLabels = useMemo(
    () =>
      Object.fromEntries(
        (config?.fields ?? [])
          .filter((field) => field.type === "relation" && field.relationType)
          .map((field) => [
            field.key,
            Object.fromEntries(
              (related.data?.[field.relationType!] ?? []).map((record) => [
                record.id,
                (field.relationLabelKeys ?? ["name"])
                  .map((key) => String(record.data[key] ?? ""))
                  .filter(Boolean)
                  .join(" · ") || record.id,
              ]),
            ),
          ]),
      ),
    [config, related.data],
  );
  const filterGroups = useMemo(
    () =>
      (config?.fields ?? []).filter(
        (field) => field.type === "select" && field.options?.length,
      ),
    [config],
  );
  const advancedFilterFields = useMemo(
    () =>
      (config?.fields ?? []).filter(
        (field) =>
          field.type !== "select" &&
          !(
            config?.type === "tasks" &&
            ["owner", "vendorId"].includes(field.key)
          ),
      ),
    [config],
  );
  const owners = useMemo(() => taskOwners(query.data ?? []), [query.data]);
  const items = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const filtered = (query.data ?? []).filter((item) => {
      const relationText = Object.entries(relationLabels)
        .map(([key, labels]) => labels[String(item.data[key] ?? "")] ?? "")
        .join(" ");
      const matchesSearch =
        !keyword ||
        `${JSON.stringify(item.data)} ${relationText}`
          .toLowerCase()
          .includes(keyword);
      const matchesFilter = Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        const field = config?.fields.find((candidate) => candidate.key === key);
        const raw = item.data[key];
        if (field?.type === "checkbox") return String(Boolean(raw)) === value;
        if (field?.type === "currency")
          return Number(raw ?? 0) / 100 === Number(value);
        if (field?.type === "number") return Number(raw ?? 0) === Number(value);
        if (
          field?.type === "select" ||
          field?.type === "relation" ||
          field?.type === "date"
        )
          return String(raw ?? "") === value;
        return String(raw ?? "")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
      const today = new Date().toISOString().slice(0, 10);
      const matchesSpecial =
        !specialView ||
        (specialView === "overdue" &&
          Boolean(item.data.dueDate) &&
          String(item.data.dueDate) < today &&
          !["已完成", "已取消"].includes(String(item.data.status))) ||
        (specialView === "unpaid" &&
          Number(item.data.actualCents ?? item.data.budgetCents ?? 0) >
            Number(item.data.paidCents ?? 0) &&
          String(item.data.paymentStatus ?? "") !== "已付清");
      return matchesSearch && matchesFilter && matchesSpecial;
    });
    return config?.type === "tasks" ? sortTasksByStatus(filtered) : filtered;
  }, [config?.type, filters, query.data, relationLabels, search, specialView]);

  if (!config) {
    return (
      <div className="error-state">
        <h2>页面不存在</h2>
        <p>请从左侧导航选择一个模块。</p>
      </div>
    );
  }

  const exportCsv = () => {
    const fields = config.fields;
    const headers = fields.map((field) => csvEscape(field.label));
    const blob = new Blob(
      [
        "\uFEFF" +
          [
            headers.join(","),
            ...(query.data ?? []).map((item) =>
              fields
                .map((field) =>
                  csvEscape(
                    field.type === "currency"
                      ? Number(item.data[field.key] ?? 0) / 100
                      : field.type === "relation"
                        ? (relationLabels[field.key]?.[
                            String(item.data[field.key])
                          ] ?? "")
                        : item.data[field.key],
                  ),
                )
                .join(","),
            ),
          ].join("\n"),
      ],
      { type: "text/csv;charset=utf-8" },
    );
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${config.path}-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const analyzeImport = (
    fileName: string,
    headers: string[],
    sourceRows: Record<string, string>[],
    mapping: Record<string, string>,
  ): ImportPreview => {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!sourceRows.length) errors.push("文件中没有可导入的数据行");
    for (const field of config.fields.filter((item) => item.required)) {
      if (!mapping[field.key])
        errors.push(`必填字段“${field.label}”尚未选择对应列`);
    }
    const normalizedRows = sourceRows.map((row, rowIndex) => {
      const data: Record<string, unknown> = {};
      for (const field of config.fields) {
        const sourceHeader = mapping[field.key];
        const raw = sourceHeader ? row[sourceHeader] : undefined;
        if (raw == null || raw === "") {
          if (field.required && sourceHeader)
            errors.push(`第 ${rowIndex + 2} 行缺少“${field.label}”`);
          continue;
        }
        if (field.type === "currency" || field.type === "number") {
          const number = Number(raw);
          if (!Number.isFinite(number)) {
            errors.push(`第 ${rowIndex + 2} 行“${field.label}”不是有效数字`);
            continue;
          }
          data[field.key] =
            field.type === "currency" ? Math.round(number * 100) : number;
        } else if (field.type === "checkbox") {
          data[field.key] = ["是", "true", "1", "yes"].includes(
            String(raw).toLowerCase(),
          );
        } else if (field.type === "relation" && field.relationType) {
          const relation = (related.data?.[field.relationType] ?? []).find(
            (record) => {
              const label = (field.relationLabelKeys ?? ["name"])
                .map((key) => String(record.data[key] ?? ""))
                .filter(Boolean)
                .join(" · ");
              return record.id === raw || label === raw;
            },
          );
          if (relation) data[field.key] = relation.id;
          else
            errors.push(
              `第 ${rowIndex + 2} 行找不到关联的“${field.label}”：${raw}`,
            );
        } else {
          if (
            field.type === "select" &&
            field.options &&
            !field.options.includes(String(raw))
          ) {
            warnings.push(
              `第 ${rowIndex + 2} 行“${field.label}”使用了列表外的值：${raw}`,
            );
          }
          data[field.key] = raw;
        }
      }
      return data;
    });
    const identityField =
      [
        "title",
        "vendorName",
        "name",
        "item",
        "risk",
        "idea",
        "segment",
        "shotName",
        "role",
        "areaItem",
        "phase",
      ].find((key) => config.fields.some((field) => field.key === key)) ??
      config.fields.find((field) => field.required)?.key;
    if (identityField) {
      const values = normalizedRows
        .map((row, index) => ({
          value: String(row[identityField] ?? ""),
          rowIndex: index,
        }))
        .filter((item) => Boolean(item.value));
      const label =
        config.fields.find((field) => field.key === identityField)?.label ??
        "主要字段";
      const seen = new Map<string, number>();
      values.forEach(({ value, rowIndex }) => {
        const first = seen.get(value);
        if (first != null)
          warnings.push(
            `第 ${rowIndex + 2} 行“${label}”与第 ${first + 2} 行重复：${value}`,
          );
        else seen.set(value, rowIndex);
      });
      const existing = new Set(
        (query.data ?? [])
          .map((item) => String(item.data[identityField] ?? "").trim())
          .filter(Boolean),
      );
      values.forEach(({ value, rowIndex }) => {
        if (existing.has(value.trim()))
          warnings.push(
            `第 ${rowIndex + 2} 行“${label}”已存在于当前项目：${value}`,
          );
      });
    }
    return {
      fileName,
      headers,
      sourceRows,
      mapping,
      rows: normalizedRows,
      sourceCount: sourceRows.length,
      errors,
      warnings,
    };
  };

  const previewCsv = async (file: File) => {
    const parsed = parseCsv(await file.text());
    const mapping = Object.fromEntries(
      config.fields.map((field) => [
        field.key,
        parsed.headers.find(
          (header) =>
            header.trim() === field.label || header.trim() === field.key,
        ) ?? "",
      ]),
    );
    setImportPreview(
      analyzeImport(file.name, parsed.headers, parsed.rows, mapping),
    );
    if (inputRef.current) inputRef.current.value = "";
  };

  const changeImportMapping = (fieldKey: string, sourceHeader: string) => {
    setImportPreview((current) => {
      if (!current) return current;
      const mapping = { ...current.mapping, [fieldKey]: sourceHeader };
      return analyzeImport(
        current.fileName,
        current.headers,
        current.sourceRows,
        mapping,
      );
    });
  };

  const downloadImportReport = () => {
    if (!importPreview) return;
    const rows = [
      ["级别", "问题"],
      ...importPreview.errors.map((issue) => ["错误", issue]),
      ...importPreview.warnings.map((issue) => ["提醒", issue]),
    ];
    const blob = new Blob(
      ["\uFEFF" + rows.map((row) => row.map(csvEscape).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" },
    );
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${config.path}-导入检查报告-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const executeImport = async () => {
    if (!importPreview || importPreview.errors.length) return;
    setImporting(true);
    const created: EntityRecord[] = [];
    try {
      for (const data of importPreview.rows) {
        const result = await api.create(params.workspaceId, config.type, data);
        created.push(result.data);
      }
      await client.invalidateQueries({
        queryKey: [params.workspaceId, config.type],
      });
      setFeedback({
        tone: "success",
        message: `已导入 ${importPreview.rows.length} 条${config.singular}`,
      });
      setImportPreview(undefined);
    } catch (error) {
      let rolledBack = 0;
      for (const item of [...created].reverse()) {
        try {
          await api.remove(
            params.workspaceId,
            config.type,
            item.id,
            item.version,
          );
          rolledBack += 1;
        } catch {
          // Leave the successfully created record visible if rollback itself fails.
        }
      }
      await client.invalidateQueries({
        queryKey: [params.workspaceId, config.type],
      });
      const failureMessage =
        error instanceof Error ? error.message : "CSV 导入失败";
      setImportPreview((current) =>
        current
          ? {
              ...current,
              errors: [
                ...current.errors,
                `提交失败：${failureMessage}；已撤回 ${rolledBack}/${created.length} 条本次新增记录`,
              ],
            }
          : current,
      );
      setFeedback({
        tone: "error",
        message: `${failureMessage}；已撤回 ${rolledBack}/${created.length} 条本次新增记录，可下载检查报告`,
      });
    } finally {
      setImporting(false);
    }
  };

  const deleteSelected = async (cascade = false) => {
    const visibleSelected = items.filter((item) => selected.has(item.id));
    if (!visibleSelected.length) return;
    try {
      for (const item of visibleSelected)
        await remove.mutateAsync({ item, cascade });
      setSelected(new Set());
      setBulkDeleteOpen(false);
      setFeedback({
        tone: "success",
        message: `已删除 ${visibleSelected.length} 条记录`,
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "批量删除失败",
      });
    }
  };

  const bulkUpdate = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const targets = items.filter((item) => selected.has(item.id));
      const updated: EntityRecord[] = [];
      for (const item of targets) {
        const result = await api.update(
          params.workspaceId,
          "tasks",
          item.id,
          patch,
          item.version,
        );
        updated.push(result.data);
      }
      return updated;
    },
    onSuccess: (updated) => {
      const byId = new Map(updated.map((item) => [item.id, item]));
      client.setQueryData<EntityRecord[]>(
        [params.workspaceId, "tasks"],
        (old) => old?.map((item) => byId.get(item.id) ?? item),
      );
      setBulkEditOpen(false);
      setSelected(new Set());
      setFeedback({
        tone: "success",
        message: `已统一更新 ${updated.length} 项任务`,
      });
    },
    onError: (error) =>
      setFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "批量编辑失败",
      }),
    onSettled: () =>
      client.invalidateQueries({ queryKey: [params.workspaceId, "tasks"] }),
  });

  const closeDetail = () => {
    setViewing(undefined);
    const parameters = new URLSearchParams(window.location.search);
    parameters.delete("record");
    window.history.replaceState(
      null,
      "",
      `${location.pathname}${parameters.size ? `?${parameters}` : ""}`,
    );
  };

  const createRelation = async (
    type: EntityType,
    data: Record<string, unknown>,
  ) => {
    const result = await api.create(params.workspaceId, type, data);
    client.setQueryData<Partial<Record<EntityType, EntityRecord[]>>>(
      relationQueryKey,
      (old) => ({
        ...(old ?? {}),
        [type]: [result.data, ...(old?.[type] ?? [])],
      }),
    );
    client.setQueryData<EntityRecord[]>([params.workspaceId, type], (old) => [
      result.data,
      ...(old ?? []),
    ]);
    return result.data;
  };

  const recordsView = config.type === "tasks" || view === "records";
  const filteredEmpty = Boolean(query.data?.length) && !items.length;
  const hasActiveConditions = Boolean(
    search || specialView || Object.values(filters).some(Boolean),
  );
  const budgetSummary =
    config.type === "budgets"
      ? (query.data ?? []).reduce(
          (summary, item) => ({
            budget: summary.budget + Number(item.data.budgetCents ?? 0),
            actual: summary.actual + Number(item.data.actualCents ?? 0),
            paid: summary.paid + Number(item.data.paidCents ?? 0),
            dueSoon:
              summary.dueSoon +
              (item.data.paymentDueDate &&
              String(item.data.paymentDueDate) >=
                new Date().toISOString().slice(0, 10) &&
              String(item.data.paymentStatus) !== "已付清"
                ? 1
                : 0),
          }),
          { budget: 0, actual: 0, paid: 0, dueSoon: 0 },
        )
      : undefined;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">婚礼筹备</span>
          <h1>{config.label}</h1>
          <p>{config.description}</p>
        </div>
        {recordsView && (
          <div className="page-header-actions">
            {config.type === "guests" && (
              <>
                <Button
                  className="secondary"
                  onClick={() => guestRegistration.mutate("copy")}
                  disabled={guestRegistration.isPending}
                >
                  <ClipboardPenLine size={17} />
                  {guestRegistration.isPending
                    ? "正在生成…"
                    : "复制自助登记链接"}
                </Button>
                <Button
                  className="secondary"
                  onClick={() => guestRegistration.mutate("qr")}
                  disabled={guestRegistration.isPending}
                >
                  <QrCode size={17} /> 自助登记二维码
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                setEditing(undefined);
                setOpen(true);
              }}
            >
              <Plus size={17} /> 新增{config.singular}
            </Button>
          </div>
        )}
      </header>

      {config.type !== "tasks" && (
        <div
          className="workspace-view-tabs"
          role="tablist"
          aria-label={`${config.label}工作区`}
        >
          <button
            className={view === "records" ? "active" : ""}
            onClick={() => setView("records")}
            role="tab"
            aria-selected={view === "records"}
          >
            {config.label}清单
          </button>
          <button
            className={view === "tasks" ? "active" : ""}
            onClick={() => setView("tasks")}
            role="tab"
            aria-selected={view === "tasks"}
          >
            筹备事项
          </button>
          <button
            className={view === "library" ? "active" : ""}
            onClick={() => setView("library")}
            role="tab"
            aria-selected={view === "library"}
          >
            事项库
          </button>
        </div>
      )}

      {recordsView ? (
        <>
          {budgetSummary && (
            <section className="budget-summary" aria-label="预算汇总">
              <div>
                <span>计划预算</span>
                <strong>{formatMoney(budgetSummary.budget)}</strong>
              </div>
              <div>
                <span>实际支出</span>
                <strong>{formatMoney(budgetSummary.actual)}</strong>
                <small>
                  {budgetSummary.budget
                    ? `已用 ${Math.round((budgetSummary.actual / budgetSummary.budget) * 100)}%`
                    : "尚未设置预算"}
                </small>
              </div>
              <div>
                <span>已付款</span>
                <strong>{formatMoney(budgetSummary.paid)}</strong>
              </div>
              <div>
                <span>待付款</span>
                <strong>
                  {formatMoney(
                    Math.max(0, budgetSummary.actual - budgetSummary.paid),
                  )}
                </strong>
                <small>{budgetSummary.dueSoon} 笔已设置下次付款日</small>
              </div>
            </section>
          )}
          <section className="toolbar">
            <div className="search-box">
              <Search size={17} />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setSelected(new Set());
                }}
                placeholder={`搜索${config.label}…`}
                aria-label={`搜索${config.label}`}
              />
            </div>
            {filterGroups.map((field) => (
              <label className="filter-select" key={field.key}>
                <Filter size={16} />
                <select
                  value={filters[field.key] ?? ""}
                  onChange={(event) => {
                    setFilters((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }));
                    setSelected(new Set());
                  }}
                  aria-label={`按${field.label}筛选`}
                >
                  <option value="">全部{field.label}</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option}>
                      {field.optionLabels?.[option] ?? option}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            {config.type === "tasks" && (
              <>
                <label className="filter-select">
                  <Filter size={16} />
                  <select
                    value={filters.owner ?? ""}
                    onChange={(event) => {
                      setFilters((current) => ({
                        ...current,
                        owner: event.target.value,
                      }));
                      setSelected(new Set());
                    }}
                    aria-label="按负责人筛选"
                  >
                    <option value="">全部负责人</option>
                    {owners.map((owner) => (
                      <option key={owner}>{owner}</option>
                    ))}
                  </select>
                </label>
                <label className="filter-select">
                  <Filter size={16} />
                  <select
                    value={filters.vendorId ?? ""}
                    onChange={(event) => {
                      setFilters((current) => ({
                        ...current,
                        vendorId: event.target.value,
                      }));
                      setSelected(new Set());
                    }}
                    aria-label="按关联供应商筛选"
                  >
                    <option value="">全部关联供应商</option>
                    {(related.data?.vendors ?? []).map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {String(vendor.data.vendorName ?? "未命名供应商")}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
            {advancedFilterFields.length > 0 && (
              <>
                {advancedFiltersOpen && (
                  <button
                    type="button"
                    className="field-filter-backdrop"
                    aria-label="关闭更多字段筛选"
                    onClick={() => setAdvancedFiltersOpen(false)}
                  />
                )}
                <details
                  className="field-filter-panel"
                  open={advancedFiltersOpen}
                  onToggle={(event) =>
                    setAdvancedFiltersOpen(event.currentTarget.open)
                  }
                >
                  <summary>
                    <Filter size={16} /> 更多字段筛选
                    {advancedFilterFields.filter((field) => filters[field.key])
                      .length > 0 && (
                      <b>
                        {
                          advancedFilterFields.filter(
                            (field) => filters[field.key],
                          ).length
                        }
                      </b>
                    )}
                  </summary>
                  <div
                    className="field-filter-grid"
                    role="group"
                    aria-label="更多字段筛选选项"
                  >
                    <header className="field-filter-mobile-header">
                      <div>
                        <span className="eyebrow">筛选任务</span>
                        <strong>更多字段筛选</strong>
                      </div>
                      <button
                        type="button"
                        aria-label="关闭更多字段筛选"
                        onClick={() => setAdvancedFiltersOpen(false)}
                      >
                        <X size={20} />
                      </button>
                    </header>
                    <div className="field-filter-fields">
                      {advancedFilterFields.map((field) => (
                        <label key={field.key}>
                          <span>{field.label}</span>
                          {field.type === "relation" ? (
                            <select
                              value={filters[field.key] ?? ""}
                              onChange={(event) => {
                                setSelected(new Set());
                                setFilters((current) => ({
                                  ...current,
                                  [field.key]: event.target.value,
                                }));
                              }}
                              aria-label={`按${field.label}筛选`}
                            >
                              <option value="">全部{field.label}</option>
                              {(related.data?.[field.relationType!] ?? []).map(
                                (record) => (
                                  <option key={record.id} value={record.id}>
                                    {relationLabels[field.key]?.[record.id] ??
                                      record.id}
                                  </option>
                                ),
                              )}
                            </select>
                          ) : field.type === "checkbox" ? (
                            <select
                              value={filters[field.key] ?? ""}
                              onChange={(event) => {
                                setSelected(new Set());
                                setFilters((current) => ({
                                  ...current,
                                  [field.key]: event.target.value,
                                }));
                              }}
                              aria-label={`按${field.label}筛选`}
                            >
                              <option value="">全部</option>
                              <option value="true">是</option>
                              <option value="false">否</option>
                            </select>
                          ) : (
                            <input
                              type={
                                field.type === "date"
                                  ? "date"
                                  : ["number", "currency"].includes(
                                        field.type ?? "",
                                      )
                                    ? "number"
                                    : "search"
                              }
                              min={field.type === "currency" ? "0" : undefined}
                              step={
                                field.type === "currency" ? "0.01" : undefined
                              }
                              value={filters[field.key] ?? ""}
                              onChange={(event) => {
                                setSelected(new Set());
                                setFilters((current) => ({
                                  ...current,
                                  [field.key]: event.target.value,
                                }));
                              }}
                              placeholder={
                                field.type === "currency"
                                  ? "人民币元"
                                  : `筛选${field.label}`
                              }
                              aria-label={`按${field.label}筛选`}
                            />
                          )}
                        </label>
                      ))}
                    </div>
                    <footer className="field-filter-mobile-footer">
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => {
                          const advancedKeys = new Set(
                            advancedFilterFields.map((field) => field.key),
                          );
                          setFilters((current) =>
                            Object.fromEntries(
                              Object.entries(current).filter(
                                ([key]) => !advancedKeys.has(key),
                              ),
                            ),
                          );
                          setSelected(new Set());
                        }}
                      >
                        清空
                      </button>
                      <button
                        type="button"
                        onClick={() => setAdvancedFiltersOpen(false)}
                      >
                        确定
                      </button>
                    </footer>
                  </div>
                </details>
              </>
            )}
            <span className="toolbar-spacer" />
            {selected.size > 0 && (
              <>
                {config.type === "tasks" && (
                  <button onClick={() => setBulkEditOpen(true)}>
                    <ClipboardPenLine size={16} /> 批量编辑{" "}
                    {items.filter((item) => selected.has(item.id)).length} 项
                  </button>
                )}
                <button
                  className="danger-action"
                  onClick={() => {
                    remove.reset();
                    setBulkDeleteOpen(true);
                  }}
                  disabled={remove.isPending}
                >
                  <Trash2 size={16} /> 删除{" "}
                  {items.filter((item) => selected.has(item.id)).length} 项
                </button>
              </>
            )}
            <input
              ref={inputRef}
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) =>
                event.target.files?.[0] &&
                void previewCsv(event.target.files[0])
              }
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={importing}
            >
              <Upload size={16} /> {importing ? "导入中…" : "导入 CSV"}
            </button>
            <button onClick={exportCsv}>
              <Download size={16} /> 导出
            </button>
          </section>
          {hasActiveConditions && (
            <div className="active-filter-notice">
              {filteredEmpty
                ? "当前搜索或筛选没有结果。"
                : `当前显示 ${items.length} 条符合条件的记录。`}
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFilters({});
                  setSpecialView("");
                  window.history.replaceState(null, "", location.pathname);
                }}
              >
                清除条件
              </button>
            </div>
          )}
          <section className="data-panel">
            {query.isLoading ? (
              <Skeleton lines={8} />
            ) : query.isError ? (
              <div className="error-state">
                <h3>加载失败</h3>
                <p>{query.error.message}</p>
                <button onClick={() => query.refetch()}>重试</button>
              </div>
            ) : (
              <EntityTable
                config={config}
                items={items}
                selected={selected}
                relationLabels={relationLabels}
                emptyTitle={filteredEmpty ? "没有符合条件的记录" : undefined}
                emptyDetail={
                  filteredEmpty ? "请调整搜索关键词或筛选条件。" : undefined
                }
                {...(!filteredEmpty
                  ? {
                      onCreate: () => {
                        setEditing(undefined);
                        setOpen(true);
                      },
                      onImport: () => inputRef.current?.click(),
                      ...(config.type !== "tasks"
                        ? { onLibrary: () => setView("library") }
                        : {}),
                    }
                  : {})}
                onSelected={setSelected}
                onEdit={(item) => {
                  setEditing(item);
                  setOpen(true);
                }}
                onView={setViewing}
                onDelete={(item) => {
                  remove.reset();
                  setDeleteTarget(item);
                }}
                {...(config.type === "guests"
                  ? {
                      onInvite: (guest: EntityRecord) =>
                        invite.mutate({ guest, action: "copy" }),
                      onQrCode: (guest: EntityRecord) =>
                        invite.mutate({ guest, action: "qr" }),
                    }
                  : {})}
              />
            )}
          </section>
        </>
      ) : (
        <ModuleTodoSection
          workspaceId={params.workspaceId}
          moduleType={config.type as TaskModuleType}
          moduleLabel={config.label}
          view={view === "library" ? "library" : "current"}
        />
      )}

      <EntityEditor
        config={config}
        entity={editing}
        open={open}
        saving={save.isPending}
        relationOptions={related.data ?? {}}
        onCreateRelation={createRelation}
        onClose={() => setOpen(false)}
        onSave={(data) => save.mutate(data)}
      />
      <TaskBulkEditor
        open={bulkEditOpen}
        count={items.filter((item) => selected.has(item.id)).length}
        saving={bulkUpdate.isPending}
        vendors={related.data?.vendors ?? []}
        onClose={() => setBulkEditOpen(false)}
        onSave={(patch) => bulkUpdate.mutate(patch)}
      />
      {viewing && (
        <EntityDetail
          item={viewing}
          config={config}
          relationLabels={relationLabels}
          members={members.data?.members ?? []}
          onClose={closeDetail}
          onEdit={() => {
            setEditing(viewing);
            closeDetail();
            setOpen(true);
          }}
        />
      )}
      {importPreview && (
        <div className="drawer-backdrop">
          <aside
            className="drawer import-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="确认导入 CSV"
          >
            <header>
              <div>
                <span className="eyebrow">导入前检查</span>
                <h2>确认导入 {importPreview.fileName}</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => setImportPreview(undefined)}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              <section className="import-mapping" aria-label="CSV 列对应关系">
                <h3>第一步：确认列对应关系</h3>
                <p>左侧是系统字段，右侧选择文件中的对应列。必填字段带有 *。</p>
                <div>
                  {config.fields.map((field) => (
                    <label key={field.key}>
                      <span>
                        {field.label}
                        {field.required ? " *" : ""}
                      </span>
                      <select
                        value={importPreview.mapping[field.key] ?? ""}
                        onChange={(event) =>
                          changeImportMapping(field.key, event.target.value)
                        }
                      >
                        <option value="">不导入此字段</option>
                        {importPreview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </section>
              <div
                className={`import-summary ${importPreview.errors.length ? "has-errors" : ""}`}
              >
                <strong>{importPreview.sourceCount} 条记录</strong>
                <span>
                  {importPreview.errors.length
                    ? `${importPreview.errors.length} 个错误，修正后才能导入`
                    : importPreview.warnings.length
                      ? `检查通过，有 ${importPreview.warnings.length} 项提醒`
                      : "格式和重复检查通过，可以安全导入"}
                </span>
              </div>
              {importPreview.errors.length > 0 && (
                <section className="import-issues" role="alert">
                  <h3>必须修正</h3>
                  <ul>
                    {importPreview.errors.slice(0, 12).map((issue, index) => (
                      <li key={`${index}-${issue}`}>{issue}</li>
                    ))}
                  </ul>
                  {importPreview.errors.length > 12 && (
                    <p>另有 {importPreview.errors.length - 12} 个错误</p>
                  )}
                </section>
              )}
              {importPreview.warnings.length > 0 && (
                <section className="import-issues warnings">
                  <h3>重复与格式提醒</h3>
                  <ul>
                    {importPreview.warnings
                      .slice(0, 12)
                      .map((warning, index) => (
                        <li key={`${index}-${warning}`}>{warning}</li>
                      ))}
                  </ul>
                  {importPreview.warnings.length > 12 && (
                    <p>
                      另有 {importPreview.warnings.length - 12}{" "}
                      项提醒，请下载完整报告。
                    </p>
                  )}
                </section>
              )}
              {(importPreview.errors.length > 0 ||
                importPreview.warnings.length > 0) && (
                <button
                  type="button"
                  className="secondary-action import-report-button"
                  onClick={downloadImportReport}
                >
                  <Download size={16} /> 下载带行号的检查报告
                </button>
              )}
              <p className="import-rollback-note">
                导入过程中如果任一条失败，系统会自动撤回本次已新增的记录。
              </p>
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => setImportPreview(undefined)}
              >
                取消
              </Button>
              <Button
                onClick={() => void executeImport()}
                disabled={Boolean(importPreview.errors.length) || importing}
              >
                {importing
                  ? "正在导入…"
                  : `确认导入 ${importPreview.rows.length} 条`}
              </Button>
            </footer>
          </aside>
        </div>
      )}
      {deleteTarget && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label={`删除${config.singular}`}
          >
            <header>
              <div>
                <span className="eyebrow">删除记录</span>
                <h2>删除这条{config.singular}？</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => {
                  remove.reset();
                  setDeleteTarget(undefined);
                }}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              {deleteReferences.length ? (
                <div className="dependency-warning" role="alert">
                  <strong>
                    这条{config.singular}还出现在以下 {deleteReferences.length}{" "}
                    条数据中：
                  </strong>
                  <ul>
                    {deleteReferences.map((reference) => (
                      <li key={`${reference.entityType}:${reference.entityId}`}>
                        <span>{reference.moduleLabel}</span>
                        <b>「{reference.recordLabel}」</b>
                        <small>{reference.relationLabel}</small>
                      </li>
                    ))}
                  </ul>
                  <p>再次确认后，上述关联数据也会一并删除。</p>
                </div>
              ) : (
                <p>
                  删除后将从当前项目中移除。若这条记录仍被其他数据使用，系统会列出关联位置供你再次确认。
                </p>
              )}
              {remove.error && !deleteReferences.length && (
                <p className="form-error">{remove.error.message}</p>
              )}
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => {
                  remove.reset();
                  setDeleteTarget(undefined);
                }}
              >
                取消
              </Button>
              <Button
                className="danger"
                onClick={() =>
                  remove.mutate({
                    item: deleteTarget,
                    cascade: deleteReferences.length > 0,
                  })
                }
                disabled={remove.isPending}
              >
                {remove.isPending
                  ? "删除中…"
                  : deleteReferences.length
                    ? "确认并一并删除"
                    : "确认删除"}
              </Button>
            </footer>
          </aside>
        </div>
      )}
      {bulkDeleteOpen && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="批量删除记录"
          >
            <header>
              <div>
                <span className="eyebrow">批量操作</span>
                <h2>
                  删除选中的{" "}
                  {items.filter((item) => selected.has(item.id)).length} 条
                  {config.singular}？
                </h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => {
                  remove.reset();
                  setBulkDeleteOpen(false);
                }}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              {deleteReferences.length ? (
                <div className="dependency-warning" role="alert">
                  <strong>
                    其中一条记录还关联以下 {deleteReferences.length} 条数据：
                  </strong>
                  <ul>
                    {deleteReferences.map((reference) => (
                      <li key={`${reference.entityType}:${reference.entityId}`}>
                        <span>{reference.moduleLabel}</span>
                        <b>「{reference.recordLabel}」</b>
                        <small>{reference.relationLabel}</small>
                      </li>
                    ))}
                  </ul>
                  <p>再次确认将继续删除剩余记录，并一并删除这些关联数据。</p>
                </div>
              ) : (
                <p>
                  只会删除当前筛选结果中已选中的记录。若存在关联，系统会列出位置供你再次确认。
                </p>
              )}
              {remove.error && !deleteReferences.length && (
                <p className="form-error">{remove.error.message}</p>
              )}
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => {
                  remove.reset();
                  setBulkDeleteOpen(false);
                }}
              >
                取消
              </Button>
              <Button
                className="danger"
                onClick={() => void deleteSelected(deleteReferences.length > 0)}
                disabled={remove.isPending}
              >
                {remove.isPending
                  ? "删除中…"
                  : deleteReferences.length
                    ? "确认并继续删除"
                    : "确认批量删除"}
              </Button>
            </footer>
          </aside>
        </div>
      )}
      {qrTarget && (
        <QrCodeDialog
          target={qrTarget}
          onClose={() => setQrTarget(undefined)}
        />
      )}
      {save.error && (
        <div className="toast error" role="alert">
          {save.error.message}
        </div>
      )}
      {feedback && (
        <div className={`toast ${feedback.tone}`} role="status">
          {feedback.tone === "success" && <CheckCircle2 size={17} />}
          {feedback.message}
        </div>
      )}
    </div>
  );
}
